import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import AdminNav from '../../components/AdminNav';
import {
  CheckCircle, XCircle, Loader2,
  User, Phone, Mail, Calendar, MapPin,
  Briefcase, FileText, Shield, Award, AlertCircle, Eye
} from 'lucide-react';
import API from '../../api/axios';

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">{label}</p>
        <p className="text-sm font-semibold text-slate-800 mt-0.5">{value || '—'}</p>
      </div>
    </div>
  );
}

export default function StaffReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { loadProfile(); }, [id]);

  const loadProfile = async () => {
    try {
      const res = await API.get(`/admin/staff/${id}/details`);
      setProfile(res.data.data);
    } catch {
      toast.error('Failed to load staff details');
      navigate('/admin/pending-staff');
    } finally { setLoading(false); }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await adminAPI.approveRegistration(id);
      toast.success('Registration approved!');
      navigate('/admin/pending-staff');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to approve'); }
    finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Please provide a rejection reason'); return; }
    setActionLoading(true);
    try {
      await adminAPI.rejectRegistration(id, rejectReason);
      toast.success('Registration rejected');
      navigate('/admin/pending-staff');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to reject'); }
    finally { setActionLoading(false); setShowRejectModal(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  if (!profile) return null;

  const isPending = profile.status === 'PENDING_APPROVAL';

  const statusColors = {
    PENDING_APPROVAL: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    ACTIVE:           'bg-green-50  border-green-200  text-green-800',
    APPROVED:         'bg-green-50  border-green-200  text-green-800',
    REJECTED:         'bg-red-50    border-red-200    text-red-800',
  };
  const statusColor = statusColors[profile.status] || statusColors.PENDING_APPROVAL;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav
        title={`${profile.firstName} ${profile.lastName}`}
        subtitle={`Profile #${id} · ${profile.status?.replace(/_/g,' ')}`}
        backPath="/admin/pending-staff"
      />

      {/* Sticky action bar — only when pending */}
      {isPending && (
        <div className="sticky top-16 z-10 bg-white border-b border-gray-100 shadow-sm">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Review the profile below before taking action.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowRejectModal(true)} disabled={actionLoading}
                className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200
                  px-5 py-2 rounded-xl font-bold text-sm hover:bg-red-100 transition disabled:opacity-50">
                <XCircle className="h-4 w-4" /> Reject
              </button>
              <button onClick={handleApprove} disabled={actionLoading}
                className="flex items-center gap-1.5 bg-green-600 text-white px-5 py-2
                  rounded-xl font-bold text-sm hover:bg-green-700 transition disabled:opacity-50 shadow-lg shadow-green-500/20">
                {actionLoading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <CheckCircle className="h-4 w-4" />}
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-5">

        {/* Status banner */}
        <div className={`rounded-xl p-4 flex items-center gap-3 border ${statusColor}`}>
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-bold text-sm">Status: {profile.status?.replace(/_/g,' ')}</p>
            <p className="text-xs opacity-75 mt-0.5">
              Registered {new Date(profile.createdAt).toLocaleDateString('en-IN', {
                day:'numeric', month:'long', year:'numeric'
              })}
            </p>
          </div>
        </div>

        {/* Personal */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
              <User className="h-4 w-4 text-teal-600" />
            </div>
            <h2 className="font-bold text-slate-800">Personal Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={User}     label="Full Name"   value={`${profile.firstName} ${profile.lastName}`} />
            <InfoRow icon={Phone}    label="Phone"       value={profile.phone} />
            <InfoRow icon={Mail}     label="Email"       value={profile.email || 'Not provided'} />
            <InfoRow icon={Calendar} label="Date of Birth" value={profile.dateOfBirth} />
            <InfoRow icon={User}     label="Gender"      value={profile.gender} />
            <InfoRow icon={Shield}   label="User ID"     value={`#${profile.userId}`} />
          </div>
        </div>

        {/* Professional */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Briefcase className="h-4 w-4 text-blue-600" />
            </div>
            <h2 className="font-bold text-slate-800">Professional Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={Briefcase} label="Domain"        value={profile.domainName} />
            <InfoRow icon={Award}     label="Staff Type"    value={profile.staffTypeName} />
            <InfoRow icon={FileText}  label="Qualification" value={profile.qualification} />
            <InfoRow icon={Calendar}  label="Experience"    value={`${profile.experienceYears} years`} />
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
              <MapPin className="h-4 w-4 text-red-500" />
            </div>
            <h2 className="font-bold text-slate-800">Address</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={MapPin} label="Address" value={profile.addressLine} />
            <InfoRow icon={MapPin} label="City"    value={profile.city} />
            <InfoRow icon={MapPin} label="State"   value={profile.state} />
            <InfoRow icon={MapPin} label="Pincode" value={profile.pincode} />
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
              <FileText className="h-4 w-4 text-purple-600" />
            </div>
            <h2 className="font-bold text-slate-800">Uploaded Documents</h2>
          </div>
          {profile.credentials && profile.credentials.length > 0 ? (
            <div className="space-y-3">
              {profile.credentials.map((cred) => (
                <div key={cred.id}
                  className="border border-gray-100 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                      ${cred.credentialType === 'AADHAAR' ? 'bg-blue-50' : 'bg-green-50'}`}>
                      <FileText className={`h-5 w-5
                        ${cred.credentialType === 'AADHAAR' ? 'text-blue-600' : 'text-green-600'}`} />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-slate-800">{cred.credentialType}</p>
                      <p className="text-xs text-gray-500 font-mono">{cred.documentNumber}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold
                      ${cred.status === 'VERIFIED'  ? 'bg-green-100 text-green-700'  :
                        cred.status === 'REJECTED'  ? 'bg-red-100   text-red-700'    :
                        'bg-yellow-100 text-yellow-700'}`}>
                      {cred.status}
                    </span>
                    {cred.documentUrl && (
                      <button
                        onClick={() => window.open(`/api/files/view?path=${cred.documentUrl}`, '_blank')}
                        className="flex items-center gap-1.5 bg-blue-50 text-blue-600 border border-blue-100
                          px-4 py-1.5 rounded-xl text-xs font-bold hover:bg-blue-100 transition">
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-400">
              <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No documents uploaded</p>
            </div>
          )}
        </div>

        {/* Referral */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
              <Award className="h-4 w-4 text-orange-500" />
            </div>
            <h2 className="font-bold text-slate-800">Referral & Platform</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={Award}  label="Referral Code"    value={profile.referralCode} />
            <InfoRow icon={Award}  label="Referred By"
              value={profile.referredByCode
                ? `${profile.referredByName || 'Unknown'} (${profile.referredByCode})`
                : 'None (direct signup)'} />
            <InfoRow icon={Shield} label="Profile Completion" value={`${profile.profileCompletionPct}%`} />
          </div>
        </div>

        {/* Bottom actions (for long pages) */}
        {isPending && (
          <div className="flex justify-end gap-3 pb-4">
            <button onClick={() => setShowRejectModal(true)} disabled={actionLoading}
              className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200
                px-6 py-3 rounded-xl font-bold text-sm hover:bg-red-100 transition disabled:opacity-50">
              <XCircle className="h-5 w-5" /> Reject
            </button>
            <button onClick={handleApprove} disabled={actionLoading}
              className="flex items-center gap-1.5 bg-green-600 text-white px-6 py-3
                rounded-xl font-bold text-sm hover:bg-green-700 transition disabled:opacity-50 shadow-lg shadow-green-500/20">
              {actionLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle className="h-5 w-5" />}
              Approve Registration
            </button>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-slate-800 mb-1">Reject Registration</h3>
            <p className="text-sm text-gray-400 mb-4">
              This will reject {profile.firstName} {profile.lastName}'s registration.
            </p>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
            <div className="flex gap-3 mt-4">
              <button onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl font-semibold text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleReject} disabled={actionLoading}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl font-bold text-sm hover:bg-red-700 disabled:opacity-50">
                {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}